import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../components/ui/Alert';
import { AuthCard } from '../../features/auth/components/AuthCard';
import { identityQueryKey } from '../../features/auth/useAuth';
import { PublicLayout } from '../../layouts/PublicLayout';
import { tokenParam } from '../../lib/search';

export const Route = createFileRoute('/$lang/verify-email')({
  validateSearch: (search: Record<string, unknown>) => ({ token: tokenParam(search['token']) }),
  component: VerifyEmailScreen,
});

/**
 * Spec 13.1's last step. Better Auth's verify endpoint is a GET that consumes the token,
 * so the screen's job is to call it once and report the outcome.
 */
function VerifyEmailScreen(): React.ReactNode {
  const { lang } = Route.useParams();
  const { token } = Route.useSearch();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'pending' | 'success' | 'failed'>(
    token === '' ? 'failed' : 'pending',
  );

  useEffect(() => {
    if (token === '') return;

    let cancelled = false;

    void fetch(`/api/auth/verify-email?token=${encodeURIComponent(token)}`, {
      credentials: 'include',
    })
      .then(async (response) => {
        if (cancelled) return;
        setStatus(response.ok ? 'success' : 'failed');
        /* Verification flips emailVerified, which the banner reads. */
        if (response.ok) await queryClient.invalidateQueries({ queryKey: identityQueryKey });
      })
      .catch(() => {
        if (!cancelled) setStatus('failed');
      });

    /*
     * The token is single-use (spec 10.3). React 18+ mounts effects twice in development,
     * and a second call would consume an already-consumed token and report failure for a
     * verification that actually succeeded — hence the cancellation flag.
     */
    return () => {
      cancelled = true;
    };
  }, [token, queryClient]);

  return (
    <PublicLayout lang={lang}>
      <AuthCard title={t('auth.verifyEmail.title')}>
        {status === 'pending' && <Alert tone="info">{t('auth.verifyEmail.pending')}</Alert>}

        {status === 'success' && (
          <div className="flex flex-col gap-4">
            <Alert tone="success">{t('auth.verifyEmail.success')}</Alert>
            <Link to="/$lang/app" params={{ lang }} className="text-sm text-accent underline">
              {t('auth.verifyEmail.continue')}
            </Link>
          </div>
        )}

        {status === 'failed' && (
          <div className="flex flex-col gap-4">
            <Alert tone="error">{t('auth.verifyEmail.failed')}</Alert>
            <Link to="/$lang/login" params={{ lang }} className="text-sm text-accent underline">
              {t('auth.login.submit')}
            </Link>
          </div>
        )}
      </AuthCard>
    </PublicLayout>
  );
}
